import { FormattedMessage } from "react-intl";
import { QuestionMarkTooltip } from "../../../../common/tooltip/question-mark-tooltip";
import dropdown_icon from "../../../../../img/dropdown_icon.svg";
import React, { useRef, useState, Fragment } from "react";
import { Box, Typography, Menu, MenuItem, styled } from "@mui/material";
import { useOutsideMenuCloserWithFunction } from "../../../../../helpers";
import { ChatDeleteDialog } from "./chat-delete-dialog";
import { useOpenClose } from "../../../../../hooks/use-open-close";
import { ChatBlockDialog } from "./chat-block-dialog";
import { useChatStore } from "../../../../../store/chatStore";

export const ChatHeader = () => {
  const [toggleChatOptions, setToggleChatOptions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Use centralized chat store
  const { chosenChat, updateChosenChat } = useChatStore();

  const chatOptionsRef = useRef(null);

  useOutsideMenuCloserWithFunction(chatOptionsRef, () =>
    setToggleChatOptions(false)
  );
  const { value: isDeleteChatOpen, toggle: toggleDeleteChat } =
    useOpenClose(false);

  const { value: isBlockChatDialogOpen, toggle: toggleBlockChatDialog } =
    useOpenClose(false);

  const haveIBlocked =
    chosenChat?.isBlockedBy?.length === 2 ||
    (chosenChat?.isBlockedBy?.length === 1 &&
      !chosenChat?.isBlockedBy
        ?.map((user) => user._id)
        .includes(chosenChat?.receiverId));

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setToggleChatOptions(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setToggleChatOptions(false);
  };

  const handleBlockChat = () => {
    handleMenuClose();
    toggleBlockChatDialog();
  };

  const handleDeleteChat = () => {
    handleMenuClose();
    toggleDeleteChat();
  };

  // Don't render if no chat is selected
  if (!chosenChat) {
    return null;
  }

  return (
    <Fragment>
      <Container ref={chatOptionsRef}>
        {chosenChat.isOnVacation && (
          <IsOnVacationWrapper>
            <OnVacationText>
              <FormattedMessage id="your-tutoring.vacation-mode-chat.tooltip" />
            </OnVacationText>
            <QuestionMarkTooltip tooltipId={"chat.vacation.hint"} />
          </IsOnVacationWrapper>
        )}
        <UsernameIconWrapper
          onClick={handleMenuClick}
        >
          <ChosenUsername title={chosenChat.chatTitle}>
            {chosenChat.chatTitle}
          </ChosenUsername>
          <DropdownIcon src={dropdown_icon} />
        </UsernameIconWrapper>
        <Menu
          anchorEl={anchorEl}
          open={toggleChatOptions}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            "& .MuiPaper-root": {
              boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
              borderRadius: "5px",
              padding: "15px",
              minWidth: "120px",
            }
          }}
        >
          <MenuItem 
            onClick={handleBlockChat}
            sx={{
              color: "#808080",
              "&:hover": {
                color: "#262626",
              },
              marginTop: "15px",
              "&:first-child": {
                marginTop: 0,
              },
            }}
          >
            {haveIBlocked ? (
              <FormattedMessage id="chat.unblock-chat" />
            ) : (
              <FormattedMessage id="chat.block-chat" />
            )}
          </MenuItem>
          <MenuItem 
            onClick={handleDeleteChat}
            sx={{
              color: "#808080",
              "&:hover": {
                color: "#262626",
              },
              marginTop: "15px",
            }}
          >
            <FormattedMessage id="chat.delete-chat" />
          </MenuItem>
        </Menu>
      </Container>
      <ChatDeleteDialog
        isOpen={isDeleteChatOpen}
        toggle={toggleDeleteChat}
        chosenChat={chosenChat}
      />
      <ChatBlockDialog
        isOpen={isBlockChatDialogOpen}
        toggle={toggleBlockChatDialog}
        chosenChat={chosenChat}
        haveIBlocked={haveIBlocked}
      />
    </Fragment>
  );
};

const Container = styled(Box)({
  position: "relative",
  display: "flex",
  width: "calc(100% - 20px)", // Account for the 10px margin on each side
  flexDirection: "column",
  marginLeft: "10px",
  marginRight: "10px",
  marginTop: "30px",
  zIndex: 1,
});

const UsernameIconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "100%",
  cursor: "pointer",
  paddingRight: "10px", // Add padding to ensure dropdown icon has space
});

const ChosenUsername = styled(Typography)({
  flex: 1,
  fontWeight: 600,
  fontSize: "20px",
  color: "#575757",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
});

const DropdownIcon = styled("img")({
  width: "24px",
  height: "24px",
  margin: "0 5px",
});

const IsOnVacationWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

const OnVacationText = styled(Typography)({
  color: "#f19191",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "19px",
  marginRight: "2px",
});
